<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useMapGetter } from 'dashboard/composables/store';
import { DropdownContainer, DropdownBody } from 'next/dropdown-menu/base';
import { dynamicTime } from 'shared/helpers/timeHelper';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
const { accountScopedUrl } = useAccount();

const notificationMeta = useMapGetter('notifications/getMeta');
const allNotifications = useMapGetter('notifications/getNotifications');
const uiFlags = useMapGetter('notifications/getUIFlags');

const unreadCount = computed(() => notificationMeta.value?.unreadCount || 0);

const badgeText = computed(() => {
  if (!unreadCount.value) return '';
  return unreadCount.value < 100 ? `${unreadCount.value}` : '99+';
});

const displayNotifications = computed(() => {
  return (allNotifications.value || []).slice(0, 15);
});

const isFetching = computed(() => uiFlags.value?.isFetching || false);

const fetchNotifications = () => {
  store.dispatch('notifications/get', { page: 1 });
};

const typeLabel = notification => {
  const key = notification.notification_type?.toUpperCase();
  const i18nKey = `INBOX.TYPES_NEXT.${key}`;
  const translated = t(i18nKey);
  if (translated !== i18nKey) return translated;

  const fallback = {
    channel_disconnected: t('NOTIFICATIONS_DROPDOWN.TYPE.CHANNEL_DISCONNECTED'),
    qr_code_needed: t('NOTIFICATIONS_DROPDOWN.TYPE.QR_NEEDED'),
    task_assigned: t('NOTIFICATIONS_DROPDOWN.TYPE.TASK_ASSIGNED'),
    deal_won: t('NOTIFICATIONS_DROPDOWN.TYPE.DEAL_WON'),
    deal_lost: t('NOTIFICATIONS_DROPDOWN.TYPE.DEAL_LOST'),
  };
  return (
    fallback[notification.notification_type] || notification.notification_type
  );
};

const notificationText = notification => {
  if (notification.push_message_title) return notification.push_message_title;
  if (notification.meta?.message) return notification.meta.message;
  if (notification.last_activity_message)
    return notification.last_activity_message;
  return '';
};

const actorMeta = notification => {
  if (notification.primary_actor_type === 'Conversation') {
    return `#${notification.primary_actor?.display_id || notification.primary_actor_id}`;
  }
  if (notification.meta?.inbox_name) return notification.meta.inbox_name;
  return '';
};

const navigateTo = notification => {
  const type = notification.primary_actor_type;
  const notifType = notification.notification_type;

  if (type === 'Conversation') {
    const convId =
      notification.primary_actor?.display_id || notification.primary_actor_id;
    router.push(accountScopedUrl(`conversations/${convId}`));
    return;
  }

  if (
    type === 'Inbox' ||
    notifType === 'channel_disconnected' ||
    notifType === 'qr_code_needed'
  ) {
    const inboxId =
      notification.primary_actor?.id || notification.primary_actor_id;
    if (inboxId) {
      router.push(accountScopedUrl(`settings/inboxes/${inboxId}`));
      return;
    }
  }

  if (
    notification.primary_actor?.type === 'captain_insight' ||
    notifType === 'captain_daily_briefing' ||
    notifType === 'captain_insight_alert'
  ) {
    router.push(accountScopedUrl('captain/brain/insights'));
    return;
  }

  if (notifType === 'task_assigned' || notifType === 'task_due_soon') {
    router.push(accountScopedUrl('crm/tasks'));
    return;
  }

  if (notifType === 'deal_won' || notifType === 'deal_lost') {
    const dealId =
      notification.primary_actor?.id || notification.primary_actor_id;
    if (dealId) {
      router.push(accountScopedUrl(`crm/board/${dealId}`));
      return;
    }
  }

  router.push(accountScopedUrl('notifications'));
};

const markRead = async notification => {
  if (!notification.read_at) {
    await store.dispatch('notifications/read', {
      id: notification.id,
      primaryActorType: notification.primary_actor_type,
      primaryActorId: notification.primary_actor_id,
      unreadCount: unreadCount.value,
    });
  }
  navigateTo(notification);
};

const markAllRead = async () => {
  await store.dispatch('notifications/readAll');
};

const goToAll = () => {
  router.push(accountScopedUrl('notifications'));
};
</script>

<template>
  <DropdownContainer class="relative">
    <template #trigger="{ toggle, isOpen }">
      <button
        class="size-8 rounded-lg hover:bg-n-stone-50 grid place-content-center text-n-stone-400 hover:text-n-stone-700 transition-colors cursor-pointer relative"
        :class="{ 'bg-n-brand/10 text-n-brand': isOpen }"
        :title="t('NOTIFICATIONS_DROPDOWN.TITLE')"
        @click="
          () => {
            toggle();
            if (!isOpen) fetchNotifications();
          }
        "
      >
        <span class="i-lucide-bell size-4" />
        <span
          v-if="badgeText"
          class="absolute -top-0.5 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold leading-[18px] text-center text-white bg-n-ruby-9 rounded-full border-[1.5px] border-white"
        >
          {{ badgeText }}
        </span>
      </button>
    </template>

    <DropdownBody
      class="top-full mt-1.5 ltr:right-0 rtl:left-0 z-50 w-[380px] max-w-[calc(100vw-1rem)]"
    >
      <!-- Header -->
      <div
        class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
      >
        <h4 class="text-sm font-semibold text-n-slate-12 m-0">
          {{ t('NOTIFICATIONS_DROPDOWN.TITLE') }}
        </h4>
        <button
          v-if="unreadCount > 0"
          class="text-xs font-medium text-n-brand hover:underline cursor-pointer"
          @click="markAllRead"
        >
          {{ t('INBOX.MENU_ITEM.MARK_ALL_READ') }}
        </button>
      </div>

      <!-- Loading -->
      <div
        v-if="isFetching && !displayNotifications.length"
        class="flex items-center justify-center py-12"
      >
        <span class="i-lucide-loader-2 size-5 text-n-slate-10 animate-spin" />
      </div>

      <!-- Notifications list -->
      <div
        v-else-if="displayNotifications.length"
        class="overflow-y-auto max-h-[380px]"
      >
        <button
          v-for="notification in displayNotifications"
          :key="notification.id"
          class="flex items-start gap-2.5 w-full px-4 py-3 text-left transition-colors border-b border-n-weak/50 last:border-0 cursor-pointer"
          :class="
            notification.read_at
              ? 'hover:bg-n-slate-2'
              : 'bg-n-blue-2/40 hover:bg-n-blue-2/70'
          "
          @click="markRead(notification)"
        >
          <span
            v-if="!notification.read_at"
            class="mt-1.5 size-2 rounded-full bg-n-brand flex-shrink-0"
          />
          <span v-else class="mt-1.5 size-2 flex-shrink-0" />
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between gap-2 mb-0.5">
              <span class="text-xs font-semibold text-n-slate-12 truncate">
                {{ typeLabel(notification) }}
              </span>
              <span class="text-[11px] text-n-slate-9 flex-shrink-0">
                {{ dynamicTime(notification.created_at) }}
              </span>
            </div>
            <p
              v-if="notificationText(notification)"
              class="text-xs text-n-slate-11 m-0 truncate"
            >
              {{ notificationText(notification) }}
            </p>
            <span
              v-if="actorMeta(notification)"
              class="text-[11px] text-n-slate-9"
            >
              {{ actorMeta(notification) }}
            </span>
          </div>
        </button>
      </div>

      <!-- Empty -->
      <div v-else class="flex flex-col items-center justify-center py-12 px-4">
        <span class="i-lucide-bell-off size-6 text-n-slate-8 mb-2" />
        <p class="text-sm text-n-slate-10 m-0">
          {{ t('NOTIFICATIONS_DROPDOWN.EMPTY') }}
        </p>
      </div>

      <!-- Footer -->
      <div class="border-t border-n-weak px-4 py-2.5">
        <button
          class="text-xs font-medium text-n-brand hover:underline cursor-pointer"
          @click="goToAll"
        >
          {{ t('NOTIFICATIONS_DROPDOWN.VIEW_ALL') }}
        </button>
      </div>
    </DropdownBody>
  </DropdownContainer>
</template>
